// Caller-identity hook for the tasks feature.
//
// GET /v1/me is the SSOT for the caller's own employee_id + identity_id, which
// the shared TasksTable compares against a row's assignee to decide whether the
// lifecycle actions (complete / dismiss / snooze) are shown at all. Kept
// feature-local on the recognition / surveys / support-tickets precedent (each
// feature owns its slice of /v1/me). The query key is feature-suffixed ON
// PURPOSE: recognition/surveys cache a DIFFERENT slice ({employee_id, roles})
// under ["me","identity"] — reusing their key would hand this hook a cached
// object with no identity_id and silently break the generic-assignee match.
//
// ADVISORY ONLY (ADR-008/ADR-017): this hook decides only what the UI SHOWS —
// the server's in-handler isAssignee assertion (routes/tasks.ts) independently
// 403s a non-assignee on every lifecycle call. A forged/stale identity dangles
// a button that then fails honestly, never widens authority.

import { useQuery } from "@tanstack/react-query";

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";

/** The slice of /v1/me the tasks feature reads. */
export interface MeIdentity {
  readonly employee_id: string | null;
  readonly identity_id: string;
}

type MeGet = (path: "/v1/me") => Promise<EnvelopedFetchResult<MeIdentity>>;

async function fetchMe(): Promise<MeIdentity> {
  const get = platformGraph.GET as unknown as MeGet;
  const data = await unwrapEnvelope(get("/v1/me"));
  return { employee_id: data.employee_id, identity_id: data.identity_id };
}

export interface MeIdentityState {
  readonly employeeId: string | null;
  readonly identityId: string | null;
  readonly isLoading: boolean;
}

/**
 * Resolve the caller's own employee/identity ids. While loading both ids are
 * null (default-closed — the table hides the assignee-only actions until the
 * real identity resolves, never flashes them on someone else's row).
 */
export function useMeIdentity(): MeIdentityState {
  const query = useQuery({
    queryKey: ["me", "identity", "tasks"],
    queryFn: fetchMe,
    staleTime: 60_000,
  });
  return {
    employeeId: query.data?.employee_id ?? null,
    identityId: query.data?.identity_id ?? null,
    isLoading: query.isLoading,
  };
}
